import {
  computed,
  DestroyRef,
  Directive,
  inject,
  InjectionToken,
  OnInit,
  Signal,
  signal,
  viewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { animate, state, style, transition, trigger } from '@angular/animations';

import { DIALOG_DATA, DialogRef, Entity, ImageModel } from '@hglobal/common';
import { TaskEntityBase } from '@pt/models';
import { distinctUntilChanged, filter } from 'rxjs';
import { Store } from '@ngrx/store';
import { ConfettiComponent } from '@pt/components';
import { AuthSelectors } from '@pt/auth';

export type DialogData<T extends Entity> = {
  task: T;
  mode: 'preview' | 'regular';
  userOnboardingId?: number;
  userMentoringId?: number;
};

export const CompletedIconAnimation = trigger('completedIcon', [
  state('notCompleted', style({ opacity: 0, transform: 'rotate(-45deg) scale(2)' })),
  state('completed', style({ opacity: 1 })),
  transition('notCompleted => completed', animate('100ms')),
]);

export const TASK_DETAILS_API = new InjectionToken<any>('TASK_DETAILS_API');

export interface BaseTask extends Entity {
  completed?: boolean;
  entity?: TaskEntityBase | null;
  image?: ImageModel;
}

@Directive()
export abstract class TaskDetailsBaseComponent<T extends BaseTask> implements OnInit {
  protected store = inject(Store);

  readonly #dialogRef = inject(DialogRef);
  readonly #destroyRef = inject(DestroyRef);
  readonly #data = inject<DialogData<T>>(DIALOG_DATA);
  readonly #taskDetailsService = inject(TASK_DETAILS_API);

  confettiComponent = viewChild.required(ConfettiComponent);
  task = signal(this.#data.task);
  isCompleted = computed(() => this.task().completed);
  isPreviewMode: boolean = this.#data.mode === 'preview';
  completedIconAnimationState = computed(() => (
    this.isCompleted() ? 'completed' : 'notCompleted'
  ));
  completedControl = new FormControl(this.isCompleted());
  userLocationId = toSignal(this.store.select(AuthSelectors.selectUserLocationId));

  abstract get taskConfirmationType(): Signal<string>;

  taskBtnKey = computed(() => {
    if (this.taskConfirmationType() === 'auto') {
      return `onboardingTask.btn.goTo.${this.task().entity.type}`;
    }

    return '';
  });

  autoTaskLink = computed(() => {
    if (this.taskConfirmationType() === 'auto') {
      switch (this.task().entity.type) {
        case 'quest':
          return ['/quests', this.userLocationId(), 'quest', this.task().entity.id];
        case 'course':
          return ['/education/courses', this.task().entity.id];
        case 'questionnaire':
          return ['/my-interview'];
      }
    }

    return [];
  });

  taskImgSrc = computed(() => {
    const task = this.task();

    if (task.entity.type === 'questionnaire') {
      return '/assets/images/questionnaire-placeholder.png';
    }

    return task.entity.mainImage?.url || task.image.url;
  });

  ngOnInit(): void {
    this.completedControl.valueChanges
      .pipe(
        filter(value => value !== this.isCompleted()),
        distinctUntilChanged(),
        takeUntilDestroyed(this.#destroyRef),
      ).subscribe(() => {
        if (!this.isPreviewMode) {
          this.completeTaskReq();
        }
      });
  }

  completeTaskReq(): void {
    this.#taskDetailsService.completeTask({
      id: this.task().id,
      userOnboardingId: this.#data.userOnboardingId,
    }).pipe(takeUntilDestroyed(this.#destroyRef)).subscribe({
      next: () => {
        this.task.update(task => (
          { ...task, completed: true }
        ));
        this.confettiComponent().shootConfetti();
      }, error: () => this.completedControl.setValue(false),
    });
  }

  cancel(): void {
    this.#dialogRef.close(!this.isPreviewMode && this.completedControl.value);
  }
}
